import classnames from 'classnames';
import IconPrev from '@scramble/design-system/icons/chevron-left.svg?react';
import IconNext from '@scramble/design-system/icons/chevron-right.svg?react';
import type { PaginationProps } from './types';
import s from './index.module.scss';

/**
 * Returns exactly 7 page slots (numbers + ellipsis markers). Pages are 1-based.
 *
 * Layouts:
 *   near start : 1  2  3  4  5  …  N
 *   middle     : 1  …  p-1  p  p+1  …  N
 *   near end   : 1  …  N-4  N-3  N-2  N-1  N
 */

function buildPages(
  currentPage: number,
  totalPages: number,
): (number | 'ellipsis-start' | 'ellipsis-end')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const nearStart = currentPage <= 4;
  const nearEnd = currentPage >= totalPages - 3;

  if (nearStart) {
    return [1, 2, 3, 4, 5, 'ellipsis-end', totalPages];
  }

  if (nearEnd) {
    return [1, 'ellipsis-start', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, 'ellipsis-start', currentPage - 1, currentPage, currentPage + 1, 'ellipsis-end', totalPages];
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  className,
}: PaginationProps) {
  if (totalPages < 2) return null;

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  const pages = buildPages(currentPage, totalPages);

  function handlePageClick(page: number) {
    if (page === currentPage) return;
    onPageChange(page);
  }

  return (
    <nav className={classnames(s.wrapper, className)} aria-label="Pagination">
      <ul className={s.container}>

        {/* Prev button */}
        <li className={s.nav}>
          <button
            type="button"
            className={classnames(s.nav_button, isFirstPage && s.nav_button_disabled)}
            onClick={() => !isFirstPage && handlePageClick(currentPage - 1)}
            disabled={isFirstPage}
            aria-label="Previous page"
          >
            <IconPrev />
          </button>
        </li>

        {/* Page numbers */}
        {pages.map((page, index) => {
          if (page === 'ellipsis-start' || page === 'ellipsis-end') {
            return (
              <li key={page} className={s.break}>
                <span className={s.break_label} aria-hidden="true">...</span>
              </li>
            );
          }

          const isActive = page === currentPage;

          return (
            <li key={`page-${page}-${index}`} className={classnames(s.page, isActive && s.page_active)}>
              <button
                type="button"
                className={s.page_button}
                onClick={() => handlePageClick(page)}
                aria-label={`Page ${page}`}
                aria-current={isActive ? 'page' : undefined}
              >
                {page}
              </button>
            </li>
          );
        })}

        {/* Next button */}
        <li className={s.nav}>
          <button
            type="button"
            className={classnames(s.nav_button, isLastPage && s.nav_button_disabled)}
            onClick={() => !isLastPage && handlePageClick(currentPage + 1)}
            disabled={isLastPage}
            aria-label="Next page"
          >
            <IconNext />
          </button>
        </li>
      </ul>
    </nav>
  );
}
